import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

export interface UploadedFile {
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  parsedPreview: string;
}

interface FileUploadProps {
  type: 'note' | 'source';
  label: string;
  description: string;
  acceptedFormats: string[];
  onFileUploaded: (file: UploadedFile) => void;
  onFileRemoved: () => void;
  uploadedFile: UploadedFile | null;
  isUploading: boolean;
  onUploadStart: () => void;
  onUploadEnd: () => void;
  required?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (mimeType: string) => {
  if (mimeType.includes('pdf')) {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11v6m0 0l-2-2m2 2l2-2" />
      </svg>
    );
  }
  if (mimeType.includes('epub')) {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    );
  }
  if (mimeType.includes('word') || mimeType.includes('docx')) {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  if (mimeType.includes('html')) {
    return (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    );
  }
  // Default text file icon
  return (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
};

export function FileUpload({
  type,
  label,
  description,
  acceptedFormats,
  onFileUploaded,
  onFileRemoved,
  uploadedFile,
  isUploading,
  onUploadStart,
  onUploadEnd,
  required = false,
}: FileUploadProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploadError(null);
    setUploadProgress(0);
    onUploadStart();

    // Validate file size (20MB max)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size exceeds 20MB limit');
      onUploadEnd();
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await fetch('/api/refine/upload', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Upload failed');
      }

      const data = await response.json();
      onFileUploaded(data);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      onUploadEnd();
      setTimeout(() => setUploadProgress(0), 500);
    }
  }, [type, onFileUploaded, onUploadStart, onUploadEnd]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: acceptedFormats.reduce((acc, format) => {
      // Map common extensions to MIME types
      const mimeMap: Record<string, string> = {
        '.txt': 'text/plain',
        '.html': 'text/html',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.pdf': 'application/pdf',
        '.epub': 'application/epub+zip',
      };
      const mime = mimeMap[format];
      if (mime) {
        acc[mime] = [format];
      }
      return acc;
    }, {} as Record<string, string[]>),
    maxFiles: 1,
    disabled: isUploading || !!uploadedFile,
  });

  const handleRemove = () => {
    setUploadError(null);
    onFileRemoved();
  };

  // Show uploaded file card
  if (uploadedFile) {
    return (
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-surface-300">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
          <span className="text-xs text-green-400 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Uploaded
          </span>
        </div>
        <div className="bg-surface-800 border border-surface-700 rounded-lg p-4 flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-primary-500/10 rounded-lg flex items-center justify-center text-primary-400">
            {getFileIcon(uploadedFile.mimeType)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-surface-100 truncate">{uploadedFile.fileName}</p>
            <p className="text-sm text-surface-400 mt-0.5">{formatFileSize(uploadedFile.fileSize)}</p>
            {uploadedFile.parsedPreview && (
              <p className="text-sm text-surface-500 mt-2 line-clamp-2">
                {uploadedFile.parsedPreview}
              </p>
            )}
          </div>
          <button
            onClick={handleRemove}
            className="flex-shrink-0 p-1.5 text-surface-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
            title="Remove file"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-surface-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      </div>

      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-all duration-200
          ${isDragActive && !isDragReject ? 'border-primary-500 bg-primary-500/10' : ''}
          ${isDragReject ? 'border-red-500 bg-red-500/10' : ''}
          ${!isDragActive && !isDragReject ? 'border-surface-700 hover:border-surface-600 hover:bg-surface-800' : ''}
          ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="space-y-3">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            <p className="text-sm text-surface-400">Uploading...</p>
            <div className="w-full bg-surface-700 rounded-full h-1.5">
              <div
                className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-surface-800 flex items-center justify-center text-surface-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-surface-300 mb-1">
              {isDragActive ? (
                isDragReject ? 'File type not supported' : 'Drop to upload'
              ) : (
                <>
                  <span className="text-primary-400 hover:text-primary-300">Click to upload</span>
                  {' or drag and drop'}
                </>
              )}
            </p>
            <p className="text-sm text-surface-500">{description}</p>
            <p className="text-xs text-surface-600 mt-2">
              Supported: {acceptedFormats.join(', ')} (max 20MB)
            </p>
          </>
        )}
      </div>

      {uploadError && (
        <p className="mt-2 text-sm text-red-400 flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {uploadError}
        </p>
      )}
    </div>
  );
}
